import {
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query'
import { useState } from 'react'

import { fetchMeta } from '../api/fetchMeta'
import { fetchPosts } from '../api/fetchPosts'
import { votePost } from '../api/votePost'
import type { PostSort, PostStatus, RoadmapPost } from '../api/types'
import { BoardState } from '../components/BoardState'
import { Hero } from '../components/Hero'
import { PostCard } from '../components/PostCard'
import { Toolbar } from '../components/Toolbar'
import { useDebouncedValue } from '../lib/useDebouncedValue'

const PAGE_SIZE = 30

export type BoardPageProps = {
  onSubmitIdea: () => void
}

export const BoardPage = ({ onSubmitIdea }: BoardPageProps): JSX.Element => {
  const queryClient = useQueryClient()
  const [sort, setSort] = useState<PostSort>('popular')
  const [status, setStatus] = useState<PostStatus | null>(null)
  const [query, setQuery] = useState('')
  const [limit, setLimit] = useState(PAGE_SIZE)
  const debouncedQuery = useDebouncedValue(query, 300)

  const postsKey = ['posts', sort, status, debouncedQuery, limit] as const

  const metaQuery = useQuery({ queryKey: ['meta'], queryFn: fetchMeta })

  const postsQuery = useQuery({
    queryKey: postsKey,
    queryFn: () =>
      fetchPosts({ sort, status, q: debouncedQuery, limit, offset: 0 }),
  })

  const voteMutation = useMutation({
    mutationFn: (postId: string) => votePost({ postId }),
    onMutate: async (postId) => {
      await queryClient.cancelQueries({ queryKey: postsKey })

      const previous = queryClient.getQueryData<RoadmapPost[]>(postsKey)

      queryClient.setQueryData<RoadmapPost[]>(postsKey, (old) =>
        old?.map((post) =>
          post.id === postId
            ? {
                ...post,
                hasVoted: !post.hasVoted,
                voteCount: post.voteCount + (post.hasVoted ? -1 : 1),
              }
            : post,
        ),
      )

      return { previous }
    },
    onError: (_error, _postId, context) => {
      if (context?.previous) {
        queryClient.setQueryData(postsKey, context.previous)
      }
    },
    onSuccess: (result, postId) => {
      queryClient.setQueryData<RoadmapPost[]>(postsKey, (old) =>
        old?.map((post) =>
          post.id === postId
            ? { ...post, hasVoted: result.hasVoted, voteCount: result.voteCount }
            : post,
        ),
      )
    },
  })

  const posts = postsQuery.data ?? []
  const canLoadMore = posts.length >= limit
  const hasFilters = status !== null || debouncedQuery.trim().length > 0

  return (
    <div>
      <Hero onSubmitIdea={onSubmitIdea} />

      <main className="mx-auto w-full max-w-5xl px-4 py-8 sm:px-8 sm:py-10">
        <Toolbar
          sort={sort}
          status={status}
          query={query}
          meta={metaQuery.data}
          onSortChange={setSort}
          onStatusChange={setStatus}
          onQueryChange={setQuery}
        />

        <div className="mt-6 flex flex-col gap-3">
          {postsQuery.isLoading ? (
            <p className="py-12 text-center text-sm text-dune-text-secondary">
              Carregando...
            </p>
          ) : null}

          {!postsQuery.isLoading && postsQuery.isError ? (
            <BoardState
              kind="error"
              onRetry={() => postsQuery.refetch()}
              onSubmitIdea={onSubmitIdea}
            />
          ) : null}

          {!postsQuery.isLoading && !postsQuery.isError && posts.length === 0 ? (
            <BoardState
              kind={hasFilters ? 'no-results' : 'empty'}
              onRetry={() => postsQuery.refetch()}
              onSubmitIdea={onSubmitIdea}
            />
          ) : null}

          {posts.map((post) => (
            <PostCard
              key={post.id}
              post={post}
              pendingVote={
                voteMutation.isPending && voteMutation.variables === post.id
              }
              onVote={(postId) => voteMutation.mutate(postId)}
            />
          ))}
        </div>

        {canLoadMore ? (
          <div className="mt-6 flex justify-center">
            <button
              type="button"
              onClick={() => setLimit((value) => value + PAGE_SIZE)}
              className="rounded-2xl border border-dune-border bg-white px-6 py-3 font-display text-sm font-medium text-dune-text transition-colors hover:border-dune-accent"
            >
              Ver mais ideias
            </button>
          </div>
        ) : null}
      </main>
    </div>
  )
}
